import { FC, useEffect, useState } from 'react';
import { of } from 'rxjs';
import Slider from 'react-slick';
import { RxUtils, Tr } from '@utils';
import { Simulation, simulationService, SimulationTaskStatus, supportService, userService } from '@services';
import { ExecutiveSummaryType } from '@models';
import { useLoggedinUser, useService } from '@hooks';
import { Grid, Stack, Tab, Tabs, Typography } from '@mui/material';
import { DarkCard, IDarkCardProps } from '@components/common';
import { EmptyEntityPageLayout } from '@components/portal/common';
import { ReactComponent as Illustration } from '@assets/illustrations/need-help.svg';
import { WideNeedHelpCard } from '../WideNeedHelpCard';
import { ExecuteSummaryVehicle } from '../ExecuteSummaryVehicle';
import { ExecuteSummarySaving } from '../ExecuteSummarySaving';
import { ExecuteSummaryInfrastructure } from '../ExecuteSummaryInfrastructure';

const SummaryCard: FC<IDarkCardProps> = (props) => {
	return (
		<DarkCard
			sx={{ flexGrow: 1, height: 1.0, spaddingX: 4, paddingBottom: 4, paddingTop: 6.5 }}
			iconContentSpacing={0.5}
			titleProps={{ variant: 'body1' }}
			subtitleProps={{ variant: 'body2' }}
			{...props}
		/>
	);
};

export interface IExecutiveSummaryLayoutProps {
	simulation?: Simulation;
}

export const ExecutiveSummaryLayout: FC<IExecutiveSummaryLayoutProps> = ({ simulation }) => {
	const loggedInUser = useLoggedinUser();
	const [selectedTab, setSelectedTab] = useState<ExecutiveSummaryType>(ExecutiveSummaryType.VEHICLES);
	const [sliderRef, setSliderRef] = useState<Slider>();
	const { data: users } = useService(() => userService.getAll(true), [simulation]);
	const { data: tasks } = useService(() => (simulation ? simulationService.getTasks(simulation) : of([])), [simulation]);

	const fulfilledVehicles = (tasks || []).filter((task) => task.status === SimulationTaskStatus.FULFILLED);
	const readinessPercentage = tasks?.length ? Math.round((fulfilledVehicles.length / tasks.length) * 100) : 0;


	useEffect(() => {
		!simulation && setSliderRef(undefined);
	}, [simulation]);

	useEffect(() => {
		if (sliderRef) {
			try {
				const index = Object.values(ExecutiveSummaryType).findIndex((type) => type === selectedTab);
				sliderRef?.slickGoTo(index >= 0 ? index : 0);
			} catch {
				// noop
			}
		}
	}, [selectedTab, sliderRef]);

	return (
		<Stack direction="column" alignItems="center" justifyContent="center" spacing={4} flexGrow={1}>
			<Grid container direction="row" spacing={4}>
				<Grid item xs={12} lg={4}>
					<SummaryCard
						pattern="circlepattern1"
						icon={<Typography variant="h3">{tasks?.length ? users?.length || 0 : 0}</Typography>}
						title={<Tr.Portal path="dashboard.analyzed-employee-card-title" />}
						subtitle={<Tr.Portal path="dashboard.analyzed-employee-card-content" />}
					/>
				</Grid>
				<Grid item xs={12} lg={4}>
					<SummaryCard
						pattern="circlepattern2"
						icon={<Typography variant="h3">{tasks?.length || 0}</Typography>}
						title={<Tr.Portal path="dashboard.analyzed-vehicle-card-title" />}
						subtitle={<Tr.Portal path="dashboard.analyzed-vehicle-card-content" />}
					/>
				</Grid>
				<Grid item xs={12} lg={4}>
					<SummaryCard
						pattern="circlepattern3"
						icon={<Typography variant="h3">{`${readinessPercentage}%`}</Typography>}
						title={<Tr.Portal path="dashboard.readiness-card-title" />}
						subtitle={<Tr.Portal path="dashboard.readiness-card-content" />}
					/>
				</Grid>
			</Grid>
			{simulation ? (
				<Stack direction="column" spacing={3} sx={{ flexGrow: 1, width: 1.0 }}>
					<Tabs
						value={Object.values(ExecutiveSummaryType).includes(selectedTab) ? selectedTab : ExecutiveSummaryType.VEHICLES}
						onChange={(_e, val) => setSelectedTab(val as ExecutiveSummaryType)}
						centered
					>
						<Tab value={ExecutiveSummaryType.VEHICLES} label={<Tr.Portal path="dashboard.tab-vehicles" />} />
						{loggedInUser?.isSellerLoggedInAsManager() && (
							<Tab value={ExecutiveSummaryType.SAVINGS} label={<Tr.Portal path="dashboard.tab-savings" />} />
						)}
						{loggedInUser?.isSellerLoggedInAsManager() && (
							<Tab value={ExecutiveSummaryType.INFRASTRUCTURE} label={<Tr.Portal path="dashboard.tab-infrastructure" />} />
						)}
					</Tabs>
					<Slider
						ref={(ref) => ref && !sliderRef && setSliderRef(ref)}
						dots={false}
						arrows={false}
						draggable={false}
						infinite
						adaptiveHeight
						speed={500}
						slidesToShow={1}
						slidesToScroll={1}
					>
						<ExecuteSummaryVehicle tasks={tasks} />
						<ExecuteSummarySaving
							savings={{
								...simulation.savings,
								yearlySavings: [...(simulation?.savings?.yearlySavings || [])].sort(
									(item1, item2) => item1.year - item2.year
								),
							}}
						/>
						<ExecuteSummaryInfrastructure infrastructure={simulation.infrastructure} />
					</Slider>
					<WideNeedHelpCard
						sx={{ marginBottom: 5 }}
						onContactUsClicked={() => RxUtils.promisify(supportService.contactUs('Title', 'Message'))}
					/>
				</Stack>
			) : (
				<EmptyEntityPageLayout
					illustration={<Illustration />}
					title={<Tr.Portal path="dashboard.need-help-title" />}
					content={<Tr.Portal path="dashboard.need-help-content" />}
					addButtonText={<Tr.Portal path="dashboard.contact-us-button" />}
					addButtonProps={{ 'data-testid': 'empty-dashboard-contact-us-button' }}
				/>
			)}
		</Stack>
	);
};
